// components/Profile.js
import React, { useState } from 'react';
import { auth, updateDisplayName, db } from '../firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, deleteUser } from 'firebase/auth';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

export default function Profile({ user, onUserNameChange, onLogout }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState(user?.displayName || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const saveName = async () => {
    if (!displayNameInput.trim()) return;
    setNameSaving(true);
    setError('');
    try {
      await updateDisplayName(auth.currentUser, displayNameInput.trim());
      if (onUserNameChange) onUserNameChange(displayNameInput.trim());
      setSuccess(t('profile.name_changed_success'));
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setNameSaving(false);
    }
  };

  const changePassword = async () => {
    if (!user) return;
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('profile.password_mismatch'));
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setError(t('profile.password_length_error'));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, passwordData.newPassword);

      setSuccess(t('profile.password_changed_success'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setSuccess(''), 5000); // Clear success after 5 seconds
    } catch (error) {
      console.error('Error changing password:', error);
      setError(t('profile.password_change_error') + ': ' + error.message);
      setTimeout(() => setError(''), 5000); // Clear error after 5 seconds
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!deletePassword) return;
    setDeleting(true);
    setError('');
    try {
      // Re-authenticate first (Firebase requires this before deletion)
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Delete all Firestore data for this user
      const uid = user.uid;
      const deleteCollection = async (colRef) => {
        const snap = await getDocs(colRef);
        await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
      };
      await deleteCollection(collection(db, 'users', uid, 'events'));
      await deleteCollection(collection(db, 'users', uid, 'sharedEvents'));
      await deleteDoc(doc(db, 'users', uid));

      // Delete the Firebase Auth account
      await deleteUser(auth.currentUser);

      if (onLogout) onLogout();
    } catch (e) {
      console.error('Error deleting account:', e);
      setError(t('profile.delete_error') + ': ' + e.message);
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-5">
          <div className="fs-1">🔒</div>
          <h4>{t('profile.not_authenticated')}</h4>
          <p>{t('profile.not_authenticated_message')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-danger border-0 mb-4" style={{ background: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success border-0 mb-4" style={{ background: 'rgba(25, 135, 84, 0.1)', color: '#198754' }}>
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </div>
      )}

      <div className="row justify-content-center">
        <div className="col-lg-6 mb-4">
          <div className="card shadow-lg border-0">
            <div className="card-header bg-white border-0 py-4">
              <h4 className="mb-0 text-primary text-center">
                <i className="fas fa-user me-2"></i>
                {t('profile.display_name')}
              </h4>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-id-badge me-1 text-primary"></i>
                    {t('profile.display_name')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={displayNameInput}
                    onChange={e => setDisplayNameInput(e.target.value)}
                    placeholder={t('profile.display_name_placeholder')}
                  />
                  <small className="text-muted">{user?.email}</small>
                </div>
                <div className="col-12 text-center">
                  <button
                    className="btn btn-primary rounded-pill px-4"
                    onClick={saveName}
                    disabled={nameSaving || !displayNameInput.trim()}
                  >
                    {nameSaving ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.saving')}</>
                    ) : (
                      <><i className="fas fa-save me-1"></i>{t('common.save')}</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-lg border-0">
            <div className="card-header bg-white border-0 py-4">
              <h4 className="mb-0 text-warning text-center">
                <i className="fas fa-key me-2"></i>
                {t('profile.change_password')}
              </h4>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-lock me-1 text-warning"></i>
                    {t('profile.current_password')}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    placeholder={t('profile.current_password_placeholder')}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-key me-1 text-warning"></i>
                    {t('profile.new_password')}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    placeholder={t('profile.new_password_placeholder')}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-check-circle me-1 text-warning"></i>
                    {t('profile.confirm_password')}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    placeholder={t('profile.confirm_password_placeholder')}
                  />
                </div>
                <div className="col-12">
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      className="btn btn-warning rounded-pill px-4"
                      onClick={changePassword}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          {t('profile.changing_password')}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-key me-1"></i>
                          {t('profile.password_change_btn')}
                        </>
                      )}
                    </button>
                  </div>
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      {t('profile.password_requirement')}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account */}
      <div className="row justify-content-center mt-2">
        <div className="col-lg-6">
          <div className="card shadow-lg border-0 border-danger" style={{ borderWidth: '2px' }}>
            <div className="card-header bg-danger bg-opacity-10 border-0 py-3">
              <h4 className="mb-0 text-danger text-center">
                <i className="fas fa-user-times me-2"></i>
                {t('profile.delete_account')}
              </h4>
            </div>
            <div className="card-body p-4 text-center">
              <p className="text-muted mb-3">{t('profile.delete_account_warning')}</p>
              <button className="btn btn-danger rounded-pill px-4" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash-alt me-1"></i>
                {t('profile.delete_account')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0 text-center d-block">
                <div className="fs-1 text-danger mb-2"><i className="fas fa-exclamation-triangle"></i></div>
                <h4 className="fw-bold text-danger">{t('profile.delete_account')}</h4>
                <button type="button" className="btn-close position-absolute top-0 end-0 m-3" onClick={() => setShowDeleteModal(false)} disabled={deleting}></button>
              </div>
              <div className="modal-body pt-2">
                <p className="text-muted text-center mb-3">{t('profile.delete_confirm_message')}</p>
                <label className="form-label fw-semibold">
                  <i className="fas fa-lock me-1 text-danger"></i>
                  {t('profile.current_password')}
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder={t('profile.current_password_placeholder')}
                  disabled={deleting}
                />
                {error && <div className="alert alert-danger mt-3 py-2 small">{error}</div>}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setError(''); }} disabled={deleting}>
                  <i className="fas fa-times me-1"></i>{t('common.cancel')}
                </button>
                <button className="btn btn-danger rounded-pill px-4" onClick={deleteAccount} disabled={deleting || !deletePassword}>
                  {deleting ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>{t('profile.deleting_account')}</>
                  ) : (
                    <><i className="fas fa-trash-alt me-1"></i>{t('profile.confirm_delete')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}